// Enhanced src/features/workos/context/WorkOSContext.jsx - OAuth with AuthKit provider (NO TAB COORDINATION)
import React, { createContext, useContext, useEffect, useState } from 'react';
import workosConfig from '../config/workos';
import { authFetch, NetworkError, networkMonitor } from '../../../utils/networkRetry';
import { tabSyncManager } from '../../../utils/tabSyncManager';
import { logger } from '../../../utils/logger';

const WorkOSContext = createContext();

// App version - increment when deploying breaking changes
const APP_VERSION = '1.0.2';

export const useWorkOS = () => {
  const context = useContext(WorkOSContext);
  if (!context) {
    throw new Error('useWorkOS must be used within a WorkOSProvider');
  }
  return context;
};

export const WorkOSProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('checking');
  const [networkStatus, setNetworkStatus] = useState({ isOnline: true, type: 'unknown' });

  // Check authentication status on mount and set up monitoring
  useEffect(() => {
    checkAuthStatus();
    
    // Set up periodic session validation (every 5 minutes)
    const interval = setInterval(validateCurrentSession, 5 * 60 * 1000);
    
    // Monitor network status
    const unsubscribeNetwork = networkMonitor.addListener(setNetworkStatus);
    
    // Set up tab synchronization
    const unsubscribeTabSync = setupTabSync();
    
    return () => {
      clearInterval(interval);
      unsubscribeNetwork();
      unsubscribeTabSync();
    };
  }, []);

  // REMOVED: Automatic broadcast of auth state changes that was causing infinite loop
  // Broadcasting now only happens on specific events (login, logout, refresh)

  // Set up tab synchronization listeners (NO COORDINATION)
  const setupTabSync = () => {
    const unsubscribers = [];

    // Handle auth state changes from other tabs
    unsubscribers.push(
      tabSyncManager.addListener('authStateChanged', (event) => {
        const { user: otherUser, isAuthenticated: otherAuth, sessionStatus: otherStatus } = event.data;
        
        // Only update if the other tab has newer/better data
        if (otherAuth && !isAuthenticated) {
          logger.log('Another tab logged in, syncing state');
          setUser(otherUser);
          setIsAuthenticated(otherAuth);
          setSessionStatus(otherStatus);
          
          // Update localStorage
          if (otherUser) {
            localStorage.setItem('workos_user', JSON.stringify(otherUser));
          }
        }
      })
    );

    // Handle logout from other tabs
    unsubscribers.push(
      tabSyncManager.addListener('userLoggedOut', (event) => {
        logger.log('User logged out in another tab, syncing logout');
        
        // Clear local state
        setUser(null);
        setIsAuthenticated(false);
        setSessionStatus('invalid');
        
        // Clear storage
        localStorage.removeItem('workos_user');
      })
    );

    // Handle session refresh from other tabs - SIMPLIFIED
    unsubscribers.push(
      tabSyncManager.addListener('sessionRefreshed', (event) => {
        logger.log('Session refreshed in another tab, updating local state');
        const { user: refreshedUser } = event.data;
        
        setUser(refreshedUser);
        setSessionStatus('valid');
        
        // Update localStorage
        localStorage.setItem('workos_user', JSON.stringify(refreshedUser));
      })
    );

    // Handle session expiry from other tabs
    unsubscribers.push(
      tabSyncManager.addListener('sessionExpired', (event) => {
        logger.log('Session expired in another tab, logging out');
        handleSessionExpiry(event.data.reason);
      })
    );

    // REMOVED: performRefresh listener - no more coordinated refresh

    // Return cleanup function
    return () => {
      unsubscribers.forEach(cleanup => cleanup());
    };
  };

  // Enhanced session validation WITHOUT tab coordination
  const validateSession = async (userData) => {
    if (!userData || !userData.accessToken) {
      logger.log('No access token found');
      return { isValid: false, reason: 'no_token' };
    }
    
    try {
      // Decode and check access token expiry
      const payload = JSON.parse(atob(userData.accessToken.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      
      logger.log(`Token expires in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes`);
      
      // If token has been expired for more than 10 minutes, don't attempt refresh
      if (timeUntilExpiry < -10 * 60 * 1000) {
        logger.log('Token expired long ago (>10 min), requiring fresh login');
        return { isValid: false, reason: 'expired_stale' };
      }
      
      // If token is expired or expires in less than 5 minutes, try refresh
      if (timeUntilExpiry < 5 * 60 * 1000) {
        logger.log('Access token expired or expiring soon, attempting refresh directly...');
        
        // REMOVED ALL COORDINATION - just refresh directly
        return await refreshSession(userData);
      }
      
      // Token is still valid for a while
      if (timeUntilExpiry > 0) {
        return { isValid: true, reason: 'valid', userData };
      } else {
        logger.log('Access token has expired');
        return { isValid: false, reason: 'expired' };
      }
      
    } catch (error) {
      console.error('Session validation error:', error);
      return { isValid: false, reason: 'invalid_token' };
    }
  };

  // Perform the actual refresh (NO COORDINATION)
  const performActualRefresh = async (userData) => {
    if (!userData.refreshToken) {
      return { success: false, error: 'No refresh token available' };
    }
    
    try {
      const response = await authFetch('/.netlify/functions/workos-refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: userData.refreshToken
        })
      }, {
        maxRetries: 2,  // Reduced from 3 for faster failure
        baseDelay: 1000  // Reduced from 2000 for faster retry
      });
      
      if (response.ok) {
        const refreshedData = await response.json();
        
        const updatedUser = {
          ...userData,
          accessToken: refreshedData.accessToken,
          refreshToken: refreshedData.refreshToken || userData.refreshToken,
          lastRefreshed: Date.now()
        };
        
        // Update local state
        setUser(updatedUser);
        localStorage.setItem('workos_user', JSON.stringify(updatedUser));
        
        // Broadcast success to other tabs (no coordination, just notification)
        tabSyncManager.broadcastSessionRefreshed(updatedUser);
        
        return { success: true, userData: updatedUser };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return { success: false, error: errorData.error || 'Refresh failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Enhanced session refresh (simplified without coordination)
  const refreshSession = async (userData) => {
    const result = await performActualRefresh(userData);
    
    if (result.success) {
      logger.log('Session refreshed successfully');
      return { isValid: true, reason: 'refreshed', userData: result.userData };
    } else {
      logger.log('Session refresh failed:', result.error);
      
      if (result.error.includes('invalid_grant') || result.error.includes('expired')) {
        return { isValid: false, reason: 'refresh_token_expired' };
      } else if (result.error.includes('rate_limit')) {
        return { isValid: false, reason: 'rate_limited' };
      } else {
        return { isValid: false, reason: 'refresh_failed' };
      }
    }
  };

  // Enhanced session validation with network awareness
  const validateCurrentSession = async () => {
    if (!user || !isAuthenticated) return;
    
    // Skip validation if offline
    if (!networkStatus.isOnline) {
      logger.log('Skipping session validation - offline');
      return;
    }
    
    logger.log('Performing periodic session validation...');
    const validation = await validateSession(user);
    
    if (!validation.isValid) {
      logger.log(`Session invalid: ${validation.reason}`);
      
      // Handle different validation failures
      switch (validation.reason) {
        case 'expired':
        case 'expired_stale':
        case 'refresh_token_expired':
          logger.log('Session expired, logging out...');
          handleSessionExpiry('expired');
          // Broadcast to other tabs
          tabSyncManager.broadcastSessionExpired('expired');
          break;
        case 'network_error':
          logger.log('Network error during validation, will retry when online');
          setSessionStatus('checking');
          break;
        default:
          logger.log('Session invalid, logging out...');
          handleSessionExpiry('invalid');
          tabSyncManager.broadcastSessionExpired('invalid');
      }
    } else {
      setSessionStatus('valid');
      // Update user data if it was refreshed
      if (validation.userData && validation.reason === 'refreshed') {
        setUser(validation.userData);
      }
    }
  };

  // Handle session expiry
  const handleSessionExpiry = (reason) => {
    setSessionStatus('expired');
    
    // Clear local state
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear stored data
    localStorage.removeItem('workos_user');
    
    logger.log('Session expired - user needs to log in again');
  };

  // Enhanced auth status check
  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      setSessionStatus('checking');
      
      // Check app version and clear old data if needed
      const storedVersion = localStorage.getItem('app_version');
      if (storedVersion && storedVersion !== APP_VERSION) {  // Added check for storedVersion existing
        logger.log(`App version changed from ${storedVersion} to ${APP_VERSION}, clearing old session data`);
        localStorage.removeItem('workos_user');
        localStorage.setItem('app_version', APP_VERSION);
      } else if (!storedVersion) {
        // First time visitor, just set the version
        localStorage.setItem('app_version', APP_VERSION);
      }
      
      // Check if we're handling a callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code && window.location.pathname === '/auth/callback') {
        setIsLoading(false);
        return;
      }
      
      // Check if user is already logged in (stored locally)
      const storedUser = localStorage.getItem('workos_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          
          // If offline, assume session is valid temporarily
          if (!networkStatus.isOnline) {
            setUser(userData);
            setIsAuthenticated(true);
            setSessionStatus('checking');
            logger.log('Offline - assuming stored session is valid temporarily');
            setIsLoading(false);
            return;
          }
          
          // Validate the session
          const validation = await validateSession(userData);
          
          if (validation.isValid) {
            setUser(validation.userData || userData);
            setIsAuthenticated(true);
            setSessionStatus('valid');
            logger.log('Valid WorkOS session found:', userData.email);
          } else {
            logger.log(`Session validation failed: ${validation.reason}`);
            
            // Handle different validation failures
            switch (validation.reason) {
              case 'expired':
              case 'expired_stale':
              case 'refresh_token_expired':
                logger.log('Stored session expired, clearing...');
                localStorage.removeItem('workos_user');
                setSessionStatus('expired');
                break;
              case 'network_error':
                // Keep user logged in but mark session as uncertain
                setUser(userData);
                setIsAuthenticated(true);
                setSessionStatus('checking');
                logger.log('Network error during validation, assuming valid for now');
                break;
              default:
                logger.log('Invalid session detected, clearing storage');
                localStorage.removeItem('workos_user');
                setSessionStatus('invalid');
            }
          }
        } catch (parseError) {
          console.error('Error parsing stored user data:', parseError);
          localStorage.removeItem('workos_user');
          setSessionStatus('invalid');
        }
      } else {
        setSessionStatus('invalid');
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      setSessionStatus('checking');
    } finally {
      setIsLoading(false);
    }
  };

  // OAUTH LOGIN WITH AUTHKIT PROVIDER - Works with OAuth Applications
  const login = async (returnTo = null) => {
    try {
      logger.log('WorkOS OAuth login initiated with AuthKit provider');
      logger.log('Using client ID:', workosConfig.clientId);
      
      // Read from localStorage if no returnTo provided
      const actualReturnTo = returnTo || localStorage.getItem('workos_return_to') || '/';
      logger.log('Using return path:', actualReturnTo);
      
      // Store return URL (in case it was passed as parameter)
      localStorage.setItem('workos_return_to', actualReturnTo);
      
      // Build OAuth URL with provider=authkit parameter
      const authUrl = new URL('https://api.workos.com/user_management/authorize');
      authUrl.searchParams.set('client_id', workosConfig.clientId);
      authUrl.searchParams.set('redirect_uri', workosConfig.redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      
      // UPDATED: Encode source in state as base64 JSON
      const stateData = {
        source: 'web',
        returnTo: actualReturnTo  // Use the actual return path
      };
      authUrl.searchParams.set('state', btoa(JSON.stringify(stateData)));
      
      authUrl.searchParams.set('provider', 'authkit');
      authUrl.searchParams.set('prompt', 'login');

      logger.log('OAuth URL with AuthKit provider:', authUrl.toString());
      window.location.href = authUrl.toString();
      
    } catch (error) {
      console.error('OAuth login error:', error);
    }
  };

  // Enhanced logout with tab coordination
  const logout = async () => {
    try {
      logger.log('WorkOS logout initiated');
      
      // Broadcast logout to other tabs immediately
      tabSyncManager.broadcastLogout();
      
      // Use unified logout endpoint
      if (user && user.sessionId) {
        try {
          logger.log('Calling unified logout function with sessionId:', user.sessionId);
          
          // UPDATED: Use unified-logout endpoint
          const response = await authFetch('/.netlify/functions/unified-logout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sessionId: user.sessionId,
              source: 'web'  // ADDED: source identifier
            })
          }, {
            maxRetries: 2,
            baseDelay: 1000
          });
          
          if (response.ok) {
            // UPDATED: Unified logout just returns success, no logoutUrl
            logger.log('Logout successful');
            
            // Clear local state first
            setUser(null);
            setIsAuthenticated(false);
            setSessionStatus('invalid');
            
            // Clear all stored data
            localStorage.clear();
            sessionStorage.clear();
            
            // Clear any cookies
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            
            // Clear browser autofill
            if (window.PasswordCredential) {
              navigator.credentials.preventSilentAccess();
            }
            
            // UPDATED: Just redirect to home, no WorkOS logout URL
            window.location.replace(window.location.origin);
            return;
          } else {
            logger.warn('Unified logout function failed, falling back to local logout');
          }
        } catch (error) {
          console.error('Error calling unified logout function:', error);
          
          // If it's a network error and we're offline, still do local logout
          if (error instanceof NetworkError) {
            logger.log('Network error during logout, proceeding with local logout');
          }
        }
      }
      
      // Fallback: Clear local session only
      logger.log('Performing local logout only');
      
      // Clear all local state
      setUser(null);
      setIsAuthenticated(false);
      setSessionStatus('invalid');
      
      // Clear all stored data
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear any cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // Clear browser autofill
      if (window.PasswordCredential) {
        navigator.credentials.preventSilentAccess();
      }
      
      logger.log('Local session cleared, redirecting to home');
      window.location.replace(window.location.origin);
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      setUser(null);
      setIsAuthenticated(false);
      setSessionStatus('invalid');
      localStorage.clear();
      window.location.replace('/');
    }
  };

  // Enhanced utility function to get a valid access token
  const getValidAccessToken = async () => {
    if (!user || !isAuthenticated) {
      throw new Error('User not authenticated');
    }
    
    const validation = await validateSession(user);
    
    if (!validation.isValid) {
      throw new Error(`Session invalid: ${validation.reason}`);
    }
    
    // Return the access token (either existing or refreshed)
    return validation.userData ? validation.userData.accessToken : user.accessToken;
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    sessionStatus,
    networkStatus,
    login,
    logout,
    setUser,
    setIsAuthenticated,
    validateCurrentSession,
    getValidAccessToken,
  };

  return (
    <WorkOSContext.Provider value={value}>
      {children}
    </WorkOSContext.Provider>
  );
};